// Anesthesia Team Control (ATC)
// Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// Proprietary and confidential. Not licensed for public use — see /LICENSE.
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import tokens from "./theme/tokens";
import "./index.css"; // shared keyframes (atcPulse — accessible go-home pulse)

// Mirror the JS design tokens into :root CSS custom properties so global
// elements (body background, scrollbar, selection) can use var(--color-...)
// without importing the JS module (D-03).
const rootVars = `
  :root {
    --color-surface-base: ${tokens.color.surface.base};
    --color-surface-card: ${tokens.color.surface.card};
    --color-surface-raised: ${tokens.color.surface.raised};
    --color-border-default: ${tokens.color.border.default};
    --color-text-primary: ${tokens.color.text.primary};
    --color-text-secondary: ${tokens.color.text.secondary};
    --color-text-muted: ${tokens.color.text.muted};
    --color-accent-primary: ${tokens.color.accent.primary};
    --color-accent-active: ${tokens.color.accent.active};
    --color-accent-closing: ${tokens.color.accent.closing};
    --color-accent-cancelled: ${tokens.color.accent.cancelled};
    --color-accent-error: ${tokens.color.accent.error};
    /* Global TV responsive-scale factor (Prompt 5). 1 everywhere by default;
       the TV board sets it from the monitor resolution + manual slider so room
       cards and modals scale harmoniously. Unset elsewhere → resolves to 1. */
    --atc-scale: 1;
  }
`;

// Global reset + token vars + shared keyframes.
// Body is light-and-airy per D-01a (overrides the legacy dark surface).
const style = document.createElement("style");
style.textContent = `
  ${rootVars}
  *{box-sizing:border-box;margin:0;padding:0;}
  body{background:var(--color-surface-base);color:var(--color-text-primary);font-family:${tokens.font.family};}
  @keyframes atcFlash { 50% { opacity: 0.25; } }
  /* Ring-only blink for controls that must stay READABLE while they blink.
     atcFlash fades the whole element to 0.25, which is fine for a status pill
     but wrong for a live alert button: its label ("Emergency active — tap to
     cancel") went half-invisible on a real phone, on the one control a user
     needs most under pressure. This animates the outline instead, so the
     attention-grab survives and the text never dims. */
  @keyframes atcRing {
    50% { outline-color: transparent; }
  }
  /* Colour-preserving blink for STATE fills (break pills, board cells).
     atcFlash drops the whole element to 25% opacity — on the mobile app's dark
     navy card a red "requested" or amber "covering" pill at 25% is
     indistinguishable from an untaken one, so for half of every second the
     board looks like nothing is happening. That is what "the colours are not
     showing on mobile" turned out to be.

     This blinks between the state colour and a DIMMER SHADE OF THE SAME
     COLOUR, supplied per element as --atc-pulse-dim. The pulse is just as
     visible, but the hue never washes out, so red still reads as red at every
     instant. steps(1) keeps the hard on/off feel of the original rather than
     a soft fade.

     NAMED atcStatePulse, not atcPulse: index.css already defines atcPulse as
     the go-home scale/opacity pulse, and a second definition of that name
     would silently replace it. */
  @keyframes atcStatePulse {
    50% { background-color: var(--atc-pulse-dim); border-color: var(--atc-pulse-dim); }
  }
  @media (prefers-reduced-motion: reduce) {
    *{animation: none !important;}
  }
`;
document.head.appendChild(style);

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
